import { useState } from "react";
import { ShareDialog } from "./ShareDialog";
import {
  unitsPerMeter,
  type MeasurementUnit,
} from "../shared/mark-presentation";
import { getPreferences } from "./preferences";
import { ExportSetDialog } from "./ExportSetDialog";
import { sourceName, setSourceScale, isSource } from "./source-scale";
import { calibration } from "../shared/geometry";
import { notify } from "./api";
import { Modal } from "./Modal";
import type { WorkspaceModel } from "./useWorkspace";
export function WorkspaceDialogs({ model }: { model: WorkspaceModel }) {
  const { editor, modal, cal, meters, setMeters } = model;
  const [unit, setUnit] = useState<MeasurementUnit>(
    () => getPreferences(model.user.id).measurementUnit,
  );
  return (
    <>
      {model.scaleRequest && (
        <Modal
          title={
            model.scaleRequest.tool === "make3d"
              ? "Set the scale to make 3D"
              : "Set scale before measuring"
          }
          close={model.cancelScaleRequest}
          initialFocus="[data-start-calibration]"
        >
          <p>
            <strong>{sourceName(model.scaleRequest.source)}</strong> has no
            drawing scale yet, so{" "}
            {model.scaleRequest.tool === "make3d"
              ? "a 3D model would come out the wrong size."
              : "real-world measurements would be inaccurate."}
          </p>
          <ol>
            <li>
              Pick both ends of a distance you already know, such as a labelled wall.
            </li>
            <li>Enter that distance and its units.</li>
          </ol>
          <p>
            {model.scaleRequest.tool === "make3d" ? (
              <>The 3D model will start building as soon as the scale is set.</>
            ) : (
              <>
                Then {model.scaleRequest.tool === "dimension" ? "Dimension" : "Area"}
                {" "}will reopen so you can measure.
              </>
            )}{" "}
            You only need to set the scale once for this drawing.
          </p>
          <div className="scale-request-actions">
            <button onClick={model.cancelScaleRequest}>Cancel</button>
            <button
              className="primary"
              data-start-calibration
              onClick={model.beginRequestedCalibration}
            >
              Pick a known distance
            </button>
          </div>
        </Modal>
      )}
      {cal && (
        <Modal
          title="Set scale · Enter the known distance"
          close={() => model.finishCalibration(false)}
          initialFocus="input"
        >
          <p>
            Step 3 of 3 · Enter the real-world distance between the two points you picked on{" "}
            <strong>{sourceName(cal.sheet)}</strong>.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              try {
                const current = editor.getShape(cal.sheet.id);
                if (!current) throw new Error("This drawing was removed.");
                const factor = calibration(
                  cal.a,
                  cal.b,
                  Number(meters) / unitsPerMeter[unit],
                );
                if (!isSource(current))
                  throw new Error("Select a drawing source.");
                setSourceScale(
                  editor,
                  current,
                  factor,
                  `Known distance: ${Number(meters)} ${unit}`,
                );
                model.finishCalibration(true);
                notify(
                  "Scale set. Your dimensions and areas now use this distance.",
                );
              } catch (e) {
                notify((e as Error).message);
              }
            }}
          >
            <label>
              Known distance
              <input
                autoFocus
                type="number"
                min=".001"
                step="any"
                required
                value={meters}
                onChange={(e) => setMeters(e.target.value)}
                placeholder="e.g. 5.40"
              />
            </label>
            <label>
              Distance units
              <select
                aria-label="Calibration units"
                value={unit}
                onChange={(e) => setUnit(e.target.value as MeasurementUnit)}
              >
                <option value="mm">mm</option>
                <option value="cm">cm</option>
                <option value="m">m</option>
              </select>
            </label>
            <button className="primary">Apply scale</button>
          </form>
        </Modal>
      )}
      {modal === "share" && <ShareDialog model={model} />}
      {modal === "export" && <ExportSetDialog model={model} />}
    </>
  );
}
